import { useCallback } from 'react';
import { createPortal } from 'react-dom';
import type { TutorialDefinition } from '../../types';
import { useTutorialStore } from '../../hooks';
import { useWidgetConfig } from '../../context';
import { SpotlightOverlay } from './SpotlightOverlay';
import { StepTooltip } from './StepTooltip';

interface TutorialPlayerProps {
  tutorial: TutorialDefinition;
}

/**
 * Active walkthrough orchestrator.
 * Renders via portal onto document.body (or config.portalContainer).
 * Manages current step, spotlight, and tooltip positioning.
 */
export function TutorialPlayer({ tutorial }: TutorialPlayerProps) {
  const config = useWidgetConfig();
  const progress = useTutorialStore((s) => s.progress[tutorial.id]);
  const nextStep = useTutorialStore((s) => s.nextStep);
  const prevStep = useTutorialStore((s) => s.prevStep);
  const skipTutorial = useTutorialStore((s) => s.skipTutorial);
  const completeTutorial = useTutorialStore((s) => s.completeTutorial);

  const currentStep = progress?.currentStep ?? 0;
  const step = tutorial.steps[currentStep];
  const isLast = currentStep === tutorial.steps.length - 1;
  const zIndex = (config.zIndex ?? 50) + 10;

  const handleNext = useCallback(() => {
    if (isLast) {
      completeTutorial();
    } else {
      nextStep();
    }
  }, [isLast, completeTutorial, nextStep]);

  const handlePrev = useCallback(() => {
    if (currentStep > 0) {
      prevStep();
    }
  }, [currentStep, prevStep]);

  if (!step) return null;

  const container = config.portalContainer ?? document.body;

  return createPortal(
    <SpotlightOverlay
      targetSelector={step.target}
      allowInteraction={step.allowInteraction}
      zIndex={zIndex}
    >
      <StepTooltip
        step={step}
        stepIndex={currentStep}
        totalSteps={tutorial.steps.length}
        targetSelector={step.target}
        onNext={handleNext}
        onPrev={handlePrev}
        onSkip={skipTutorial}
        zIndex={zIndex + 1}
      />
    </SpotlightOverlay>,
    container,
  );
}
